import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const ESTADO_UI = {
  dentro: { label: 'Trabajando', badge: 'badge-dentro', dot: 'bg-green-500', emoji: '🟢' },
  pausa:  { label: 'En Pausa',   badge: 'badge-pausa',  dot: 'bg-yellow-500', emoji: '🟡' },
  fuera:  { label: 'Fuera',      badge: 'badge-fuera',  dot: 'bg-gray-400',   emoji: '⚪' },
};

function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' });
}

const TIPO_LABEL = {
  entrada: 'Entró',
  entrada_desplazamiento: '🚗 Entró (desplazamiento)',
  salida: 'Salió',
  inicio_pausa: 'Pausa iniciada',
  fin_pausa: 'Reanudó trabajo',
};

export default function PanelControl({ onVerFichajes }) {
  const [plantilla, setPlantilla] = useState([]);
  const [stats, setStats] = useState(null); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        api.get('/admin/estado-plantilla'),
        api.get('/admin/stats'),
      ]);
      setPlantilla(pRes.data.plantilla);
      setStats(sRes.data);
      setLastUpdate(new Date());
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const dentro   = plantilla.filter(t => t.estado === 'dentro').length;
  const enPausa  = plantilla.filter(t => t.estado === 'pausa').length;
  const fuera    = plantilla.filter(t => t.estado === 'fuera').length;

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Cargando panel...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Resumen */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card text-center">
          <div className="text-3xl font-black text-green-600">{dentro}</div>
          <div className="text-xs text-gray-500 mt-1">Trabajando</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-black text-yellow-600">{enPausa}</div>
          <div className="text-xs text-gray-500 mt-1">En Pausa</div>
        </div>
        <div className="card text-center">
          <div className="text-3xl font-black text-gray-500">{fuera}</div>
          <div className="text-xs text-gray-500 mt-1">Fuera</div>
        </div>
      </div>

      {/* Header con refresh */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-700">Estado de la Plantilla</h3>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-gray-400">
              Actualizado {lastUpdate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={fetchData}
            className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          >
            ↻ Actualizar
          </button>
        </div>
      </div>

      {/* Grid de trabajadores */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {plantilla.map((t) => {
          const ui = ESTADO_UI[t.estado];
          return (
            <div
              key={t.id}
              className="card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onVerFichajes && onVerFichajes(t)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-800 truncate">
                    {t.nombre} {t.apellidos}
                  </div>
                  <div className="text-xs text-gray-400 truncate">@{t.username}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={ui.badge}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ui.dot}`} />
                    {ui.label}
                  </span>
                  {t.vacacionesHoy && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                      🏖️ Vacaciones
                    </span>
                  )}
                </div>
              </div>

              {t.ultimoFichaje && (
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  <span className="font-medium">{TIPO_LABEL[t.ultimoFichaje.tipo]}</span>
                  {' '}a las{' '}
                  <span className="font-mono">{fmtTime(t.ultimoFichaje.timestamp)}</span>
                </div>
              )}

              <div className="mt-2 text-xs text-mgh-blue-900 font-medium hover:underline">
                Ver fichajes →
              </div>
            </div>
          );
        })}
      </div>

      {plantilla.length === 0 && (
        <div className="card text-center py-8 text-gray-400">
          No hay trabajadores activos
        </div>
      )}
    </div>
  );
}
